/* eslint-disable max-classes-per-file */
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject, throwError } from "rxjs";
import { catchError, map, shareReplay, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import {
    getLoanStatusDisplay,
    PagedList
} from "../modules/shared/models/api-response.model";
import { buildExportFilename } from "../modules/shared/utils/export-helpers";
import { AuthService } from "./auth.service";

const API_URL = environment.api;

export interface ILoanPreview {
    accountId: string;
    agentName: string;
    agentPhone1: string;
    loanId: string;
    collateralId: string;
    loanNumber: string;
    borrowerName1: string;
    coverageTypeName: string;
    loanStatusName: string;
    insuranceStatusName: string;
    insuranceEffectiveDate: Date;
    insuranceExpirationDate: Date;
    insuranceExpirationDateText: string;
    insuranceExposureReason: string;
    insuranceCoverageAmount: number;
    comprehensiveDeductible: number;
    collisionDeductible: number;
    deficiencies: IDeficiency[];
    createdAt: Date;
    insurer: string;
    waiveExpirationDate: Date;
    documentId: string;
    documentFileUrl: string;
    isWatchedByCurrentUser: boolean;

    // for reporting lienholder deficiencies on the exception report
    lienholderName: string;
}

export interface ILoan {
    accountId: string;
    accountSubsite: string;
    loanId: string;
    number: string;
    status: string;
    balance: number;
    balanceDate: Date;
    openDate: Date;
    paymentAmount: number;
    paymentDueDate: Date;
    paymentFrequency: string;
    lienPosition: string;
    maturityDate: Date;
    maturityDateText: string;
    interestRate: number;
    loanOfficerName: string;
    loanOfficerCode: string;
    loanCondition: string;
    loanSource: string;
    programType: string;
    borrower: IAgent;
    borrowerName1: string;
    borrowerName2: string;
    borrowerPhone1: string;
    borrowerPhone2: string;
    borrowerPhone3: string;
    borrowerEmail: string;
    borrowerAddress1: string;
    borrowerAddress2: string;
    borrowerCity: string;
    borrowerState: string;
    borrowerZip: string;
    coborrower: IAgent;
    coBorrowerName1: string;
    coBorrowerName2: string;
    coBorrowerPhone1: string;
    coBorrowerPhone2: string;
    coBorrowerPhone3: string;
    coBorrowerEmail: string;
    coBorrowerAddress1: string;
    coBorrowerAddress2: string;
    coBorrowerCity: string;
    coBorrowerState: string;
    coBorrowerZip: string;
    collaterals: Array<ICollateral>;
    updateDate: Date;
    accountName: string;
    collateralTypeCode: string;
    departmentTypeCode: string;
    departmentTypeName: string;
    namedInsured: string;
    insurer: string;
    policyNumber: string;
    documentSource: string;
    comprehensiveDeductible: string;
    collisionDeductible: string;
    effectiveDate: Date;
    expirationDate: Date;
    documentDate: Date;
    agentName: string;
    agentPhone1: string;
    agentPhone2: string;
    agentEmail: string;
    agentAddress1: string;
    agentAddress2: string;
    agentCity: string;
    agentState: string;
    agentZip: string;
}

export interface ILoanPagedResp {
    limit: number;
    offset: number;
    total: number;
    data: Array<IListedLoan>;
}

export interface IListedLoan {
    accountId: string;
    accountName: string;
    balance: number;
    borrower: string;
    collateralDescription: string;
    collateralId: string;
    collateralVin: string;
    collateralYear: string;
    coverage: string;
    coverageTypeName: string;
    currentStageName: string;
    decisionDate: Date;
    insuranceStatus: string;
    insuranceStatusName: string;
    issueDate: Date;
    lienholderName: string;
    loanBalance: number;
    loanBorrowerName1: string;
    loanClassificationName: string;
    loanId: string;
    loanNumber: string;
    loanOpenDate: Date;
    loanStatusCode: string;
    loanStatusName: string;
    nextStageName: string;
    number: string;
    phone: string;
    reason: string;
    reasonName: string;
    status: string;
    totalPremium: number;
    trimmedAccountId: number;
}

export interface IInsurance {
    loanId: string;
    collateralId: string;
    insurer: string;
    documentDate: Date;
    documentFileUrl: string;
    documentId: string;
    documentSource: string;
    coverageAmount: number;
    coverageTypeCode: string;
    coverageTypeName: string;
    comprehensiveDeductible: number;
    collisionDeductible: number;
    deficiencyReason: string;
    effectiveDate: Date;
    createDate: Date;
    expirationDate: Date;
    expirationDateText: string;
    insuranceStatusCode: string;
    insuranceStatusName: string;
    policyNumber: string;
    namedInsured: string;
    agent: IAgent;
    deficiencies: IDeficiency[];
    exposureReasonName: string;
    productLine: string;
    loanConditionCode: string;
    loanConditionName: string;
    isWatchedByCurrentUser: boolean;
}

export class Insurance implements IInsurance {
    loanId: string;
    collateralId: string;
    insurer: string;
    documentDate: Date;
    documentFileUrl: string;
    documentId: string;
    documentSource: string;
    coverageAmount: number;
    coverageTypeCode: string;
    coverageTypeName: string;
    comprehensiveDeductible: number;
    collisionDeductible: number;
    deficiencyReason: string;
    effectiveDate: Date;
    createDate: Date;
    expirationDate: Date;
    expirationDateText: string;
    insuranceStatusCode: string;
    insuranceStatusName: string;
    policyNumber: string;
    namedInsured: string;
    agent: IAgent;
    deficiencies: IDeficiency[];
    exposureReasonName: string;
    productLine: string;
    documentReady: boolean;

    // only for forced insurance invoice history cards
    policyLetterId: string;
    policyLetterDescription: string;
    policyLetterFileUrl: string;

    // only for forced placed insurance cards
    coverageStartDate: Date;
    coverageEndDate: Date;
    coverageDateString: string;

    lapseStartDate: Date;
    lapseEndDate: Date;
    lapsePeriod: string;

    liabilityLimit: number;
    forcedPremium: number;
    earnedPremium: number;
    ownRefundedPremium: number | null;
    refundedPremium: number;
    cancelledAmount: number;
    forcedCardHeader: string;

    loanConditionCode: string;
    loanConditionName: string;

    isWatchedByCurrentUser: boolean;

    public get premiumForDisplay(): number {
        if (this.forcedCardHeader === "Premium Refunded") {
            return this.ownRefundedPremium;
        } else if (
            this.forcedCardHeader === "Premium Cancelled" ||
            this.forcedCardHeader === "Premium Cancelled (Supplemental)"
        ) {
            return this.cancelledAmount;
        } else {
            // Premium Paid, Premium Issued
            return this.forcedPremium;
        }
    }

    static fromJSON(obj: any): Insurance {
        let temp = new Insurance();
        Object.assign(temp, obj);
        if (obj.documentDate) {
            temp.documentDate = new Date(obj.documentDate);
        }
        if (obj.expirationDate) {
            temp.expirationDate = new Date(obj.expirationDate);
        }
        if (obj.effectiveDate) {
            temp.effectiveDate = new Date(obj.effectiveDate);
        }
        if (obj.coverageStartDate) {
            temp.coverageStartDate = new Date(obj.coverageStartDate);
        }
        if (obj.coverageEndDate) {
            temp.coverageEndDate = new Date(obj.coverageEndDate);
        }
        if (obj.lapseStartDate) {
            temp.lapseStartDate = new Date(obj.lapseStartDate);
        }
        if (obj.lapseEndDate) {
            temp.lapseEndDate = new Date(obj.lapseEndDate);
        }
        if (temp.documentId) {
            temp.documentFileUrl = `${API_URL}/api/v1/collaterals/${
                temp.collateralId
            }/documents/${temp.documentId.replace(/#/g, "%23")}.pdf`;
        }
        temp.documentReady = obj.documentReady ? obj.documentReady : false;

        return temp;
    }
}

export class ForcedInsuranceItemDetail {
    cancelDate: string;
    cancelReason: string;
    cancelAmount: number;
    refundDate: string;
    refundAmount: number;
}

export class ForcedInsurance {
    forcedInsuranceId: number;
    carrier: string;
    policyNumber: string;
    policyStatusName: string;
    effectiveDate: Date;
    expirationDate: Date;
    term: string;
    issueDate: Date;
    paidDate: Date;
    hasInvoicedPolicy: boolean;
    netPremium = 0;
    liabilityLimit = 0;
    totalPremium = 0;

    lapseStartDate: Date;
    lapseEndDate: Date;
    lapsePeriod: string;
    coverageDateString: string;

    refundedPremium: number;

    cancelled: ForcedInsuranceItemDetail[] = [];
    supplementalCancelled: ForcedInsuranceItemDetail[] = [];

    static fromJSON(obj: any): ForcedInsurance {
        let temp = new ForcedInsurance();
        let date: Date;
        Object.assign(temp, obj);

        if (obj.effectiveDate) {
            temp.effectiveDate = new Date(obj.effectiveDate);
        }
        if (obj.expirationDate) {
            temp.expirationDate = new Date(obj.expirationDate);
        }
        if (obj.issueDate) {
            temp.issueDate = new Date(obj.issueDate);
        }
        if (obj.lapseStartDate) {
            temp.lapseStartDate = new Date(obj.lapseStartDate);
        }
        if (obj.lapseEndDate) {
            temp.lapseEndDate = new Date(obj.lapseEndDate);
        }
        if (obj.paidDate) {
            temp.paidDate = new Date(obj.paidDate);
        }

        if (obj.cancelled && obj.cancelled.length > 0) {
            obj.cancelled.forEach((c) => {
                c.cancelDate = c.cancelDate.split(" ")[0];
                c.refundDate = c.refundDate.split(" ")[0];
            });
            temp.cancelled = obj.cancelled;
        }

        if (obj.supplementalCancelled && obj.cancelled.length > 0) {
            obj.supplementalCancelled.forEach((sc) => {
                sc.cancelDate = sc.cancelDate.split(" ")[0];
                sc.refundDate = sc.refundDate.split(" ")[0];
            });
            temp.supplementalCancelled = obj.supplementalCancelled;
        }

        return temp;
    }
}

export class LoanLetter {
    collateralId: string;
    coverageType: string;
    createdAt: Date;
    description: string;
    filename: string;
    fileUrl: string;
    isReprint: boolean;
    loanId: string;
    loanLetterId: string;
    fileReady: boolean;

    static fromJSON(obj: any): LoanLetter {
        let temp = new LoanLetter();
        Object.assign(temp, obj);
        if (obj.createdAt) {
            temp.createdAt = new Date(obj.createdAt);
        }
        temp.fileUrl = `${API_URL}/api/v1/loan-letters/${temp.loanLetterId}.pdf`;
        temp.fileReady = obj.fileReady ? obj.fileReady : false;
        return temp;
    }
}

export class PolicyLetter {
    collateralId: string;
    coverageType: string;
    createdAt: Date;
    description: string;
    filename: string;
    fileUrl: string;
    isReprint: boolean;
    loanId: string;
    policyLetterId: string;
    policyNumber: string;
    fileReady: boolean;

    static fromJSON(obj: any): PolicyLetter {
        let temp = new PolicyLetter();
        Object.assign(temp, obj);
        if (obj.createdAt) {
            temp.createdAt = new Date(obj.createdAt);
        }
        temp.fileUrl = `${API_URL}/api/v1/policy-letters/${temp.policyLetterId}.pdf`;

        //temp.fileReady = (obj.fileReady) ? obj.fileReady : false;
        temp.fileReady = true;
        return temp;
    }
}

export interface IAgent {
    name: string;
    name2: string;
    phone1: string;
    phone2: string;
    phone3: string;
    email: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
}

export interface ICollateral {
    accountId: string;
    loanId: string;
    collateralId: string;
    collateralCode: string;
    collateralDescription: string;
    collateralPercentage: number;
    collateralVin: string;
    collateralYear: string;
    collateralAddress1: string;
    collateralAddress2: string;
    collateralCity: string;
    collateralState: string;
    collateralZip: string;
    collateralFloodZone: string;
    loanNumber: string;
    loanConditionName: string;
    insuranceStatusName: string;
    insuranceCoverageType: string;
    insuranceExpirationDate: Date;
    insuranceExpirationDateText: string;
    productLine: string;
}

export interface IDeficiency {
    createDate: Date;
    code: string;
    description: string;
    isActive: boolean;
    item: string;
    noticeStage: string | null;
}

export class InsuranceHistory {
    insuranceHistoryId: string;
    accountId: string;
    collateralId: string;
    itemId: string;
    itemName: string;
    insuranceCompany: string;
    insuranceStatusCode: string;
    loanNumber: string;
    policyNumber: string;
    entryNumber: string;
    enteredAt: Date;
    enteredBy: string;
    coverageAmount: number;
    premiumAmount: number;
    premiumDueOn: Date;
    premiumPaidOn: Date;
    premiumPaidThrough: Date;
    replacementCostValueAmount: number;
    associationCoverageAmount: number;
    associationUnits: string;
    additionalDue: string;
    comprehensiveDeductible: string;
    collisionDeductible: string;
    deductible3: string;
    deductible3Percentage: string;
    miscellaneous1: string;
    miscellaneous2: string;
    miscellaneous3: string;
    expirationDate: string;
    effectiveDate: Date;
    waivedOn: Date;
    mailedOn: Date;
    rateCode: string;
    insufficientCoverage: string;
    formTypeCode: string;
    borrowerName: string;
    borrowerAlpha: string;
    floodZoneCode: string;
    agentCode: string;
    amBestRating: string;
    transactionTypeCode: string;
    documentId: string;
    undocumentName: string;
    undocumentEnteredAt: Date;
    payee: string;
    notStage: string;
    notStageAsOf: Date;
    isEscrow: boolean;
    isKeyFag: boolean;

    static fromJSON(obj: any): InsuranceHistory {
        let temp = new InsuranceHistory();
        Object.assign(temp, obj) as InsuranceHistory;

        if (obj.enteredAt) {
            temp.enteredAt = new Date(obj.enteredAt);
        }
        if (obj.premiumDueOn) {
            temp.premiumDueOn = new Date(obj.premiumDueOn);
        }
        if (obj.premiumPaidOn) {
            temp.premiumPaidOn = new Date(obj.premiumPaidOn);
        }
        if (obj.premiumPaidThrough) {
            temp.premiumPaidThrough = new Date(obj.premiumPaidThrough);
        }
        if (obj.effectiveDate) {
            temp.effectiveDate = new Date(obj.effectiveDate);
        }
        if (obj.waivedOn) {
            temp.waivedOn = new Date(obj.waivedOn);
        }
        if (obj.mailedOn) {
            temp.mailedOn = new Date(obj.mailedOn);
        }
        if (obj.undocumentEnteredAt) {
            temp.undocumentEnteredAt = new Date(obj.undocumentEnteredAt);
        }
        if (obj.notStageAsOf) {
            temp.notStageAsOf = new Date(obj.notStageAsOf);
        }
        return temp;
    }
}

export class LoanNote {
    accountId: string;
    loanId: string;
    noteText: string;
    createdBy: string;
    createdAt: Date;
    createdAtString: string;
    isUrgent: string;

    // only used for claim notes
    claimId: string;
    claimType: string;

    // only for invoice notes
    invoiceId: string;
    invoiceNumber: string;
    invoicePaymentType: string;
    invoicePremium: string;

    forcedInfo: ForcedInsurance;

    static fromJSON(obj: any): LoanNote {
        let note = new LoanNote();
        Object.assign(note, obj);
        if (obj.createdAt) {
            note.createdAt = new Date(obj.createdAt);
        }

        return note;
    }
}

export class RelatedItem {
    accountId: string;
    collateralId: string;
    createdAtString: string;
    loanId: string;
    createdAt: Date;
    documentId: string;
    documentFileUrl: string;
    documentReady: boolean;
    description: string;

    static fromJSON(obj: any): RelatedItem {
        let temp = new RelatedItem();
        Object.assign(temp, obj);
        if (obj.createdAt) {
            temp.createdAt = new Date(obj.createdAt);
        }
        if (temp.documentId) {
            temp.documentFileUrl = `${API_URL}/api/v1/collaterals/${
                temp.collateralId
            }/documents/${temp.documentId.replace(/#/g, "%23")}.pdf`;
        }

        return temp;
    }
}

export class FutureEvent {
    eventCode: string;
    eventName: string;
    eventDate: Date;
    deficiencyType: string;

    static fromJSON(obj: any): FutureEvent {
        let fe = new FutureEvent();
        Object.assign(fe, obj);

        if (fe.eventDate) {
            fe.eventDate = new Date(fe.eventDate);
        }

        return fe;
    }
}

export class MissingInfoItem {
    accountId: string;
    trimmedAccountId: string;
    loanId: string;
    collateralId: string;
    loanNumber: number;
    borrowerName: string;
    collateralCode: string;
    missingFields: string;
    missingFieldCategory: string;
    responseDate: Date;
    responseDaysLeft: number;
    loanOpenDate: Date;
    loanStatusCode: string;
    loanStatusName: string;
    coverageTypeName: string;
    departmentTypeName: string;
    loanClassificationName: string;
    submittedAt: Date;
    isSubmitted: boolean;
    collateralDescription: string;
    insuranceDeficiencies: string;

    static fromJSON(obj: any): MissingInfoItem {
        let ir = new MissingInfoItem();
        Object.assign(ir, obj);

        if (ir.loanOpenDate) {
            ir.loanOpenDate = new Date(ir.loanOpenDate);
        }

        if (ir.responseDate) {
            ir.responseDate = new Date(ir.responseDate);
        }

        let now = new Date();
        if (ir.responseDate) {
            const diffTime = ir.responseDate.getTime() - now.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            ir.responseDaysLeft = diffDays > 0 ? diffDays : 0;
        } else {
            ir.responseDaysLeft = 0;
        }

        ir.isSubmitted = ir.submittedAt ? true : false;

        switch (ir.loanStatusCode) {
            case "X":
            case "A":
            case "Z":
                ir.loanStatusName = "Active";
                break;
            case "W":
            case "T":
            case "D":
            case "t":
            case "P":
                ir.loanStatusName = "Closed";
                break;
            default:
                ir.loanStatusName = "";
        }

        return ir;
    }
}

export class MissingInfoSubmission {
    collateralId: string;
    submittedAt: Date;
    missingFields: string;
    isDeleted: boolean;

    static fromJSON(obj: any): MissingInfoSubmission {
        let ir = new MissingInfoSubmission();
        Object.assign(ir, obj);

        if (ir.submittedAt) {
            ir.submittedAt = new Date(ir.submittedAt);
        }

        return ir;
    }
}

export class UploadedDocument {
    accountId: string;
    collateralId: string;
    fileName: string;
    sharepointFilename: string;
    uploadType: string;
    noteText: string;
    submittedBy: number;
    submittedByEmail: string;
    submittedByName: string;
    submittedAt: Date;
    submittedAtString: string;

    documentReady: boolean;

    static fromJSON(obj: any): UploadedDocument {
        let uds = new UploadedDocument();
        Object.assign(uds, obj);

        if (uds.sharepointFilename) {
            uds.sharepointFilename = obj.sharepointFilename.replace(
                /@/g,
                "%40"
            );
        }

        if (uds.submittedAt) {
            uds.submittedAt = new Date(uds.submittedAt + "Z");
        }

        return uds;
    }
}

export interface ISharePointUpdateStatusRequest {
    accountId: string;
    accountName: string;
    borrowerName: string;
    collateralId: string;
    collateralDescription: string;
    loanNumber: string;
    status: string;
    statusDetails: string;
    note: string;
    vin: string;
    deficiencyId: string | null;
    file: File | null;
    addToWatchlist: boolean | null;
}

@Injectable()
export class LoanService {
    constructor(
        public http: HttpClient,
        private _authService: AuthService
    ) {
        this.changes = new BehaviorSubject(null);
        this.docUploaded = new Subject<any>();
    }

    changes: Subject<any>;
    docUploaded: Subject<any>;

    private _tempLoan: ILoan | null = null;
    get tempLoan(): ILoan | null {
        let _tempLoan = this._tempLoan;
        this._tempLoan = null;
        return _tempLoan;
    }
    set tempLoan(l: ILoan | null) {
        this._tempLoan = l;
    }

    private get _defaultOptions() {
        return { headers: this._headers };
    }
    private get _headers(): HttpHeaders {
        let h = new HttpHeaders();
        h = h.set("Authorization", `bearer ${this._authService.accessToken}`);
        h = h.set("Content-Type", "application/json");
        return h;
    }

    getRefundEstimate(
        collateralId: string,
        policyNumber: string,
        cancelledDate: Date
    ) {
        const headers = {
            params: {
                cancelledDate: cancelledDate.toLocaleDateString("en-US")
            },
            headers: this._headers
        };

        return this.http.get<number>(
            `${API_URL}/api/v1/collaterals/${collateralId}/${policyNumber}/refundEstimate`,
            headers
        );
    }

    getFutureEvents(collateralId: string): Observable<FutureEvent[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/future-events/${collateralId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => FutureEvent.fromJSON(elem));
                })
            );
    }

    getCoverageTypes(
        accountId: string
    ): Observable<{ name: string; code: string }[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/coverage-types/${accountId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getCoverageTypeFilters(): Observable<{ name: string; code: string }[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/coverage-types/filters`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getDeficiencyTypes(
        exclude: string | null = null
    ): Observable<{ name: string; code: string }[]> {
        let p = {};

        if (exclude) {
            p["exclude"] = exclude;
        }
        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/deficiency-types`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getEventTypes(
        exclude: string | null = null
    ): Observable<{ name: string; code: string }[]> {
        let p = {};

        if (exclude) {
            p["exclude"] = exclude;
        }
        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/event-types`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLoanConditions() {
        const options = { headers: this._headers };
        return this.http.get<{ name: string; code: string }[]>(
            `${API_URL}/api/v1/loan-conditions`,
            options
        );
    }

    getExposureReasons(): Observable<{ name: string; code: string }[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/reports/exposure-reasons`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getExposureReport(
        offset: number = 0,
        limit: number = 10,
        sort: string | null = "+borrower",
        search: string | null = null,
        status: string[] | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        customTypes: string[] | null = null,
        coverageTypes: string[] | null = null,
        insuranceStatuses: string[] | null = null,
        exposureReasons: string[] | null = null,
        loanConditions: string[] | null = null
    ): Observable<ILoanPagedResp> {
        let p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (search) {
            p["filter"] = search;
        }
        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }
        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }
        // default to all loan statuses except "t,T"
        p["loan-statuses"] = "X,A,W,D,P,Z";
        if (customTypes && customTypes.includes("ICL")) {
            p["loan-statuses"] += ",t,T";
        }
        if (customTypes && customTypes.length > 0) {
            let c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }
        if (coverageTypes && coverageTypes.length > 0) {
            p["coverages"] = coverageTypes.join(",");
        }
        if (exposureReasons && exposureReasons.length > 0) {
            p["reasons"] = exposureReasons.join(",");
        }
        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }
        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        } else {
            sort = "+borrower";
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }
        if (insuranceStatuses) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }
        let options = { headers: this._headers, params: p };

        return this.http
            .get(`${API_URL}/api/v1/reports/exposure`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res.offset = res.offset / res.limit;
                    res.data = res.data.map((l: IListedLoan) => {
                        l.loanStatusName = getLoanStatusDisplay(
                            l.loanStatusCode
                        );
                        return l;
                    });
                    return res;
                })
            );
    }

    getExposureExport(
        columns,
        format,
        sort: string | null = "+borrower",
        search: string | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        customTypes: string[] | null = null,
        coverageTypes: string[] | null = null,
        insuranceStatuses: string[] | null = null,
        exposureReasons: string[] | null = null,
        loanConditions: string[] | null = null
    ): Observable<Blob> {
        let p = { format: format };

        if (search) {
            p["filter"] = search;
        }
        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }
        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }
        // default to all loan statuses except "t,T"
        p["loan-statuses"] = "X,A,W,D,P,Z";
        if (customTypes && customTypes.includes("ICL")) {
            p["loan-statuses"] += ",t,T";
        }
        if (customTypes && customTypes.length > 0) {
            let c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }
        if (coverageTypes && coverageTypes.length > 0) {
            p["coverages"] = coverageTypes.join(",");
        }
        if (exposureReasons && exposureReasons.length > 0) {
            p["reasons"] = exposureReasons.join(",");
        }
        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }
        if (sort) {
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        } else {
            sort = "+borrower";
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        }
        if (insuranceStatuses) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }

        let options = {
            headers: this._headers,
            params: p,
            responseType: "blob" as "json"
        };

        return this.http
            .post<Blob>(
                `${API_URL}/api/v1/reports/exposure/export`,
                columns,
                options
            )
            .pipe(
                map((res) => {
                    var url = window.URL.createObjectURL(res);
                    var a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename("exposure_report", this._authService.claims.accountName, format);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    getExceptionReport(
        offset: number = 0,
        limit: number = 10,
        sort: string | null = "+borrower",
        search: string | null = null,
        status: string[] | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        customTypes: string[] | null = null,
        coverageTypes: string[] | null = null,
        insuranceStatuses: string[] | null = null,
        deficiencies: string[] | null = null,
        loanConditions: string[] | null = null
    ): Observable<ILoanPagedResp> {
        let p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (search) {
            p["filter"] = search;
        }
        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }
        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }
        // default to all loan statuses except "t,T"
        p["loan-statuses"] = "X,A,W,D,P,Z";
        if (customTypes && customTypes.includes("ICL")) {
            p["loan-statuses"] += ",t,T";
        }
        if (customTypes && customTypes.length > 0) {
            let c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }
        if (coverageTypes && coverageTypes.length > 0) {
            p["coverages"] = coverageTypes.join(",");
        }
        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }
        if (deficiencies && deficiencies.length > 0) {
            p["deficiencies"] = deficiencies.join(",");
        }
        if (sort) {
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        } else {
            sort = "+decisionDate";
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        }
        if (insuranceStatuses) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }

        let options = { headers: this._headers, params: p };

        return this.http
            .get(`${API_URL}/api/v1/reports/exception`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res.offset = res.offset / res.limit;
                    res.data = res.data.map((l: IListedLoan) => {
                        if (l.decisionDate) {
                            l.decisionDate = new Date(l.decisionDate);
                        }
                        l.loanStatusName = getLoanStatusDisplay(
                            l.loanStatusCode
                        );
                        return l;
                    });
                    return res;
                })
            );
    }

    getExceptionExport(
        columns,
        format,
        sort: string | null = "+borrower",
        search: string | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        customTypes: string[] | null = null,
        coverageTypes: string[] | null = null,
        insuranceStatuses: string[] | null = null,
        deficiencies: string[] | null = null,
        loanConditions: string[] | null = null
    ): Observable<Blob> {
        let p = { format: format };

        if (search) {
            p["filter"] = search;
        }
        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }
        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }
        // default to all loan statuses except "t,T"
        p["loan-statuses"] = "X,A,W,D,P,Z";
        if (customTypes && customTypes.includes("ICL")) {
            p["loan-statuses"] += ",t,T";
        }
        if (customTypes && customTypes.length > 0) {
            let c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }
        if (coverageTypes && coverageTypes.length > 0) {
            p["coverages"] = coverageTypes.join(",");
        }
        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }
        if (deficiencies && deficiencies.length > 0) {
            p["deficiencies"] = deficiencies.join(",");
        }
        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        } else {
            sort = "+decisionDate";
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }
        if (insuranceStatuses) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }

        let options = {
            headers: this._headers,
            params: p,
            responseType: "blob" as "json"
        };

        return this.http
            .post<Blob>(
                `${API_URL}/api/v1/reports/exception/export`,
                columns,
                options
            )
            .pipe(
                map((res) => {
                    var url = window.URL.createObjectURL(res);
                    var a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename("exception_report", this._authService.claims.accountName, format);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    getForcingReport(
        offset: number = 0,
        limit: number = 10,
        sort: string | null = "+borrower",
        search: string | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        customTypes: string[] | null = null,
        coverageTypes: string[] | null = null,
        insuranceStatuses: string[] | null = null,
        issueDate: any | null = null,
        loanConditions: string[] | null = null
    ): Observable<ILoanPagedResp> {
        let p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (search) {
            p["filter"] = search;
        }
        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }
        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }
        // default to all loan statuses except "t,T"
        p["loan-statuses"] = "X,A,W,D,P,Z";
        if (customTypes && customTypes.includes("ICL")) {
            p["loan-statuses"] += ",t,T";
        }
        if (customTypes && customTypes.length > 0) {
            let c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }
        if (coverageTypes && coverageTypes.length > 0) {
            p["coverages"] = coverageTypes.join(",");
        }
        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }
        if (sort) {
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        } else {
            sort = "+borrower";
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        }
        if (issueDate && issueDate.begin && issueDate.end) {
            p["start-at"] = issueDate.begin.toISOString().split("T")[0];
            p["end-at"] = issueDate.end.toISOString().split("T")[0];
        }
        if (insuranceStatuses) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }
        let options = { headers: this._headers, params: p };

        return this.http
            .get(`${API_URL}/api/v1/reports/forcing`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res.offset = res.offset / res.limit;
                    res.data = res.data.map((l: IListedLoan) => {
                        if (l.issueDate) {
                            // test to fix safari date issue
                            let dateStr = l.issueDate
                                .toString()
                                .slice(0, 10)
                                .replace(/-+/g, "/");
                            l.issueDate = new Date(dateStr);
                        }
                        l.loanStatusName = getLoanStatusDisplay(
                            l.loanStatusCode
                        );
                        return l;
                    });
                    return res;
                })
            );
    }
    getForcingExport(
        columns,
        format,
        sort: string | null = "+borrower",
        search: string | null = null,
        status: string[] | null = null,
        accounts: string[] | null = null,
        departments: string[] | null = null,
        customTypes: string[] | null = null,
        coverageTypes: string[] | null = null,
        insuranceStatuses: string[] | null = null,
        issueDate: any | null = null,
        loanConditions: string[] | null = null
    ): Observable<Blob> {
        let p = { format: format };

        if (search) {
            p["filter"] = search;
        }
        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }
        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }
        // default to all loan statuses except "t,T"
        p["loan-statuses"] = "X,A,W,D,P,Z";
        if (customTypes && customTypes.includes("ICL")) {
            p["loan-statuses"] += ",t,T";
        }
        if (customTypes && customTypes.length > 0) {
            let c = customTypes
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["custom-types"] = c.join(",");
        }
        if (coverageTypes && coverageTypes.length > 0) {
            p["coverages"] = coverageTypes.join(",");
        }
        if (loanConditions && loanConditions.length > 0) {
            p["loan-conditions"] = loanConditions.join(",");
        }
        if (sort) {
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        } else {
            sort = "+borrower";
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        }
        if (issueDate && issueDate.begin && issueDate.end) {
            p["start-at"] = issueDate.begin.toISOString().split("T")[0];
            p["end-at"] = issueDate.end.toISOString().split("T")[0];
        }
        if (insuranceStatuses) {
            p["insurance-statuses"] = insuranceStatuses.join(",");
        }
        let options = {
            headers: this._headers,
            params: p,
            responseType: "blob" as "json"
        };

        return this.http
            .post<Blob>(
                `${API_URL}/api/v1/reports/forcing/export`,
                columns,
                options
            )
            .pipe(
                map((res) => {
                    var url = window.URL.createObjectURL(res);
                    var a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename("forcing_report", this._authService.claims.accountName, format);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    checkLoanExists(id: string): Observable<boolean> {
        return this.http
            .get(`${API_URL}/api/v1/loans/${id}/exists`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map(() => {
                    return true;
                }),
                catchError((error) => {
                    return throwError("The loan selected could not be found.");
                })
            );
    }

    setCollateralWatchedStatus(collateralId: string, isWatched: boolean) {
        return this.http.post<boolean>(
            `${API_URL}/api/v1/collaterals/${collateralId}/watched`,
            isWatched,
            this._defaultOptions
        );
    }

    getPolicyLetterUrl(
        collateralId: string,
        policyNumber: string
    ): Observable<string> {
        return this.http
            .get(
                `${API_URL}/api/v1/collaterals/${collateralId}/policy-letter/${policyNumber}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: PolicyLetter) => {
                    return `${API_URL}/api/v1/policy-letters/${res.policyLetterId.replace(
                        /#/g,
                        "%23"
                    )}.pdf`;
                }),
                catchError((err) => of(null))
            );
    }

    getLoan(id: string): Observable<ILoan> {
        return this.http
            .get(`${API_URL}/api/v1/loans/${id}`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return this._mapLoan(res);
                })
            );
    }

    getLoanCollateral(loanId: string): Observable<Array<ICollateral>> {
        return this.http
            .get(
                `${API_URL}/api/v1/collaterals/${loanId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    let t: Array<ICollateral> = res.map((elem) =>
                        this._mapCollateral(elem)
                    );
                    // t.push(Object.assign({},t[0]));
                    // t[t.length-1].collateralId = "XX"
                    // t[t.length-1].year = "2099";
                    return t;
                })
            );
    }

    getLoanPreview(
        collateralId: string,
        deficiencyId: string | null = null
    ): Observable<ILoanPreview> {
        let url = `${API_URL}/api/v1/collaterals/${collateralId}/preview`;

        // Only applies to the Exception Report to report lienholder deficiencies
        if (deficiencyId) {
            url += `?deficiencyId=${deficiencyId}`;
        }

        return this.http
            .get(url, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return this._mapLoanPreview(res);
                })
            );
    }

    getLoanExceptionPreview(
        collateralId: string,
        deficiencyId: string
    ): Observable<ILoanPreview> {
        return this.http
            .get(
                `${API_URL}/api/v1/ /${collateralId}/preview/${deficiencyId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return this._mapLoanPreview(res);
                })
            );
    }

    getInsuranceDetails(collateralId: string): Observable<IInsurance[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/collaterals/${collateralId}/insurance`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return this._mapInsurance(res);
                })
            );
    }

    getForcedInsurance(collateralId: string): Observable<ForcedInsurance[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/forced-insurance/${collateralId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res
                        .map((elem) => ForcedInsurance.fromJSON(elem))
                        .sort((a, b) => {
                            return (
                                a.lapseStartDate < b.lapseStartDate ? 1
                                : a.lapseStartDate > b.lapseStartDate ? -1
                                : 0
                            );
                        });
                })
            );
    }

    generateForcedInsuranceReceipt(
        collateralId: string,
        policyNumber: string
    ): Observable<Blob> {
        return this.http.get(
            `${API_URL}/api/v1/forced-insurance/${collateralId}/receipts/${encodeURIComponent(policyNumber)}.pdf`,
            {
                headers: this._headers,
                responseType: "blob"
            }
        );
    }

    addRoles(userId: number, roles: Array<number>): Observable<any> {
        return this.http
            .post(
                `${API_URL}/api/v1/users/${userId}/roles`,
                roles,
                this._defaultOptions
            )
            .pipe(shareReplay());
    }

    getInsuranceHistory(collateralId: string): Observable<InsuranceHistory[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/insurance-history/${collateralId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => InsuranceHistory.fromJSON(elem));
                })
            );
    }

    getLoanNotes(collateralId: string): Observable<LoanNote[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/loan-notes/${collateralId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => LoanNote.fromJSON(elem));
                })
            );
    }

    // note.loanId is acctually the collateralId here, since the route was updated.
    createLoanNote(note: LoanNote) {
        let h = this._headers;
        h.set("Content-Type", "text");
        let options = { headers: h };
        return this.http
            .post(
                `${API_URL}/api/v1/loan-notes/${note.loanId}`,
                `"${note.noteText}"`,
                options
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return LoanNote.fromJSON(res);
                })
            );
    }

    exceptionStatusUpdates(note: ISharePointUpdateStatusRequest) {
        return this.http
            .post(
                `${API_URL}/api/v1/status-updates/exception`,
                note,
                this._defaultOptions
            )
            .pipe(shareReplay());
    }

    noteStatusUpdates(note: ISharePointUpdateStatusRequest) {
        return this.http
            .post(
                `${API_URL}/api/v1/status-updates/note`,
                note,
                this._defaultOptions
            )
            .pipe(shareReplay());
    }

    sendEmailNoticeToBorrower(
        collateralId: string,
        to: string,
        cc: string[],
        subject: string,
        body: string
    ) {
        const requestBody = {
            collateralId,
            to,
            cc,
            subject,
            body
        };

        return this.http.post(
            `${API_URL}/api/v1/status-updates/send-notice`,
            requestBody,
            this._defaultOptions
        );
    }

    getItemNotes(collateralId: string): Observable<LoanNote[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/item-notes/${collateralId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map((elem) => LoanNote.fromJSON(elem));
                })
            );
    }

    uploadLoanFile(accountId: string, file: File): Observable<any> {
        let h = this._headers;
        h = h.set("Content-Type", "multipart/form-data");
        h = h.delete("Content-Type");

        const options = { headers: h };
        const formData = new FormData();

        formData.append("accountId", accountId);
        formData.append("file", file, file.name);

        return this.http
            .post(`${API_URL}/api/v1/documents/loan`, formData, options)
            .pipe(
                tap(() => {
                    this.docUploaded.next("upload");
                })
            );
    }

    uploadInsurance(accountId: string, file: File): Observable<unknown> {
        let h = this._headers;
        h = h.set("Content-Type", "multipart/form-data");
        h = h.delete("Content-Type");

        const options = { headers: h };
        const formData = new FormData();

        formData.append("accountId", accountId);
        formData.append("file", file, file.name);

        return this.http.post(
            `${API_URL}/api/v1/documents/insurance`,
            formData,
            options
        );
    }

    uploadCollateralFile(
        note: ISharePointUpdateStatusRequest
    ): Observable<any> {
        let h = this._headers;
        h = h.set("Content-Type", "multipart/form-data");
        h = h.delete("Content-Type");

        let options = { headers: h };
        let formData = new FormData();

        if (note.file) {
            formData.append("file", note.file, note.file.name);
        }

        if (note.deficiencyId) {
            formData.append("deficiencyId", note.deficiencyId);
        }

        formData.append("accountId", note.accountId);
        formData.append("accountName", note.accountName);
        formData.append("borrowerName", note.borrowerName);
        formData.append("collateralDescription", note.collateralDescription);
        formData.append("loanNumber", note.loanNumber);
        formData.append("status", note.status);
        formData.append("collateralId", note.collateralId);
        formData.append("statusDetails", note.statusDetails);
        formData.append("note", note.note);
        formData.append("vin", note.vin);
        formData.append(
            "addToWatchlist",
            note.addToWatchlist ? "true" : "false"
        );

        return this.http
            .post(
                `${API_URL}/api/v1/status-updates/proof-of-insurance`,
                formData,
                options
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getUploadHistory(
        offset = 0,
        limit = 10,
        sort: string | null = "+borrower",
        search: string | null = null,
        accounts: string[] | null = null,
        uploadTypes: string[] | null = null,
        loanDate: Partial<{ begin: Date; end: Date }> | null = null
    ): Observable<PagedList<UploadedDocument>> {
        let p = {
            offset: `${offset * limit}`,
            limit: `${limit}`,
            accountId: `${this._authService.claims.accountIds}`
        };

        if (search) {
            p["filter"] = search;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (uploadTypes && uploadTypes.length > 0) {
            p["upload-types"] = uploadTypes.join(",");
        }

        if (loanDate && loanDate.begin && loanDate.end) {
            p["start-at"] = loanDate.begin.toISOString().split("T")[0];
            loanDate.end.setHours(23, 59, 59);
            p["end-at"] = loanDate.end.toISOString().split("T")[0];
        }

        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        } else {
            p["sort"] = "-submittedAt";
        }

        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/documents/submission-history`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res.offset = res.offset / res.limit;
                    res.data = res.data.map((elem) =>
                        UploadedDocument.fromJSON(elem)
                    );
                    return res;
                })
            );
    }

    checkInsuranceDocumentPreview(filename: string): Observable<boolean> {
        let options = { headers: this._headers, responseType: "blob" } as any;
        return this.http
            .get(`${API_URL}/api/v1/documents/insurance/${filename}`, options)
            .pipe(shareReplay())
            .pipe(
                map(() => {
                    return true;
                }),
                catchError(() => {
                    return of(false);
                })
            );
    }

    getInsuranceDocumentPreview(filename: string) {
        let options = { headers: this._headers, responseType: "blob" } as any;
        return this.http
            .get(`${API_URL}/api/v1/documents/insurance/${filename}`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    verifyCollateralDocumentId(
        collateralId: string,
        documentId: string
    ): Observable<boolean> {
        return this.http
            .head(
                `${API_URL}/api/v1/collaterals/${collateralId}/documents/${documentId.replace(
                    /#/g,
                    "%23"
                )}.pdf`,
                {
                    observe: "response"
                }
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.ok;
                }),
                catchError((error) => of(false))
            );
    }

    verifyLetterId(letterType: string, id: string): Observable<boolean> {
        return this.http
            .get(
                `${API_URL}/api/v1/${letterType}/${id.replace(/#/g, "%23")}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res !== null ? true : false;
                }),
                catchError((error) => {
                    return of(false);
                })
            );
    }

    getMissingInfoExport(
        columns,
        format,
        sort: string | null = "+responseDate",
        search: string | null = null,
        accounts: string[] | null = null,
        loanClassifications: string[] | null = null,
        departments: string[] | null = null,
        coverageTypes: string[] | null = null,
        loanDate: any | null = null,
        submittedOnly = false
    ): Observable<Blob> {
        let p = { format: format };

        if (search) {
            p["filter"] = search;
        }
        if (accounts && accounts.length > 0) {
            p["lenders"] = accounts.join(",");
        }
        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }
        if (loanClassifications && loanClassifications.length > 0) {
            let c = loanClassifications
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["loan-classifications"] = c.join(",");
        }
        if (coverageTypes && coverageTypes.length > 0) {
            p["coverages"] = coverageTypes.join(",");
        }
        if (sort) {
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        } else {
            sort = "+responseDate";
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        }
        if (loanDate && loanDate.begin && loanDate.end) {
            p["start-at"] = loanDate.begin.toISOString().split("T")[0];
            p["end-at"] = loanDate.end.toISOString().split("T")[0];
        }

        if (submittedOnly) {
            p["submitted-only"] = submittedOnly;
        }
        let options = {
            headers: this._headers,
            params: p,
            responseType: "blob" as "json"
        };

        return this.http
            .post<Blob>(
                `${API_URL}/api/v1/missing-info/export`,
                columns,
                options
            )
            .pipe(
                map((res) => {
                    var url = window.URL.createObjectURL(res);
                    var a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename("missing_information_report", this._authService.claims.accountName, format);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    submitInformationRequest(
        collateralId: string,
        missingFields: string
    ): Observable<boolean> {
        let options = { headers: this._headers, observe: "response" } as any;
        let submission = {
            collateralId: collateralId,
            submittedAt: null,
            missingFields: missingFields,
            isDeleted: false
        };

        return this.http
            .post(
                `${API_URL}/api/v1/missing-info/submissions`,
                submission,
                options
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.ok;
                }),
                catchError(() => of(false))
            );
    }

    getMissingInfoReport(
        offset = 0,
        limit = 10,
        sort: string | null = "+responseDate",
        search: string | null = null,
        accounts: string[] | null = null,
        loanClassifications: string[] | null = null,
        departments: string[] | null = null,
        coverageTypes: string[] | null = null,
        loanDate: any | null = null,
        submittedOnly: boolean = false
    ): Observable<PagedList<MissingInfoItem>> {
        const p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (search) {
            p["filter"] = search;
        }

        if (accounts && accounts.length > 0) {
            p["lenders"] = accounts.join(",");
        }

        if (departments && departments.length > 0) {
            p["departments"] = departments.join(",");
        }

        if (loanClassifications && loanClassifications.length > 0) {
            let c = loanClassifications
                .filter((c) => c !== "ICL")
                .filter((c) => c != null);
            p["loan-classifications"] = c.join(",");
        }

        if (coverageTypes && coverageTypes.length > 0) {
            p["coverages"] = coverageTypes.join(",");
        }

        if (loanDate && loanDate.begin && loanDate.end) {
            p["start-at"] = loanDate.begin.toISOString().split("T")[0];
            p["end-at"] = loanDate.end.toISOString().split("T")[0];
        }

        if (submittedOnly) {
            p["submitted-only"] = submittedOnly;
        }

        if (sort) {
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        } else {
            sort = "+responseDate";
            if (sort.charAt(0) === "+") sort = sort.substr(1, sort.length);
            p["sort"] = sort;
        }

        let options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/missing-info`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res.offset = res.offset / res.limit;
                    res.data = res.data.map((elem) =>
                        MissingInfoItem.fromJSON(elem)
                    );
                    return res;
                })
            );
    }

    getFileExtensions(
        uploadType: string
    ): Observable<{ extension: string; uploadType: string }[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/file-extensions/${uploadType}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    addFileExtension(
        extension: string,
        uploadType: string
    ): Observable<string[]> {
        let body = {
            extension: extension,
            uploadType: uploadType
        };

        let options = { headers: this._headers, observe: "response" } as any;
        return this.http
            .post(`${API_URL}/api/v1/file-extensions`, body, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.ok;
                })
            );
    }

    removeFileExtension(
        extension: string,
        uploadType: string
    ): Observable<string[]> {
        let body = {
            extension: extension,
            uploadType: uploadType
        };

        let options = {
            headers: this._headers,
            observe: "response",
            body: JSON.stringify(body)
        } as any;
        return this.http
            .delete(`${API_URL}/api/v1/file-extensions`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.ok;
                })
            );
    }

    private _dateOrString(temp: any) {
        let x = new Date(temp);
        if (isNaN(x.getTime())) {
            return temp;
        }
        return x;
    }

    private _mapLoanPreview(l: any): ILoanPreview {
        let temp = l as ILoanPreview;

        temp.documentId = l.documentId;

        if (temp.documentId) {
            temp.documentFileUrl = `${API_URL}/api/v1/collaterals/${
                temp.collateralId
            }/documents/${temp.documentId.replace(/#/g, "%23")}.pdf`;
        }

        if (l.insuranceEffectiveDate) {
            temp.insuranceEffectiveDate = new Date(l.insuranceEffectiveDate);
        }

        if (l.insuranceExpirationDate) {
            temp.insuranceExpirationDate = new Date(l.insuranceExpirationDate);
        }

        if (l.waiveExpirationDate) {
            temp.waiveExpirationDate = new Date(l.waiveExpirationDate);
        }

        if (l.insuranceDeficiencies) {
            let d = l.insuranceDeficiencies.split(",");
            temp.deficiencies = d.map((elem) => {
                let obj = { description: elem } as IDeficiency;
                return obj;
            });
        } else {
            temp.deficiencies = [];
        }

        return temp;
    }

    private _mapCollateral(c: any): ICollateral {
        let temp = c as ICollateral;
        if (!temp.insuranceStatusName) {
            temp.insuranceStatusName = "N/A";
        }
        return temp;
    }

    private _mapLoan(l: any): ILoan {
        let temp = l as ILoan;

        if (l.balanceDate) {
            temp.balanceDate = new Date(l.balanceDate);
        }

        if (l.openDate) {
            temp.openDate = new Date(l.openDate);
        }
        if (l.paymentDueDate) {
            temp.paymentDueDate = new Date(l.paymentDueDate);
        }

        if (l.maturityDate) {
            temp.maturityDate = new Date(l.maturityDate);
        }

        let a: IAgent = {} as IAgent;
        a.name = l.borrowerName1;
        a.name2 = l.borrowerName2;
        a.phone1 = l.borrowerPhone1;
        a.phone2 = l.borrowerPhone2;
        a.phone2 = l.borrowerPhone3;
        a.email = l.borrowerEmail;
        a.address1 = l.borrowerAddress1;
        a.address2 = l.borrowerAddress2;
        a.city = l.borrowerCity;
        a.state = l.borrowerState;
        a.zip = l.borrowerZip;
        temp.borrower = a;

        temp.coborrower = {
            name: l.coBorrowerName1,
            name2: l.coBorrowerName2,
            phone1: l.coBorrowerPhone1,
            phone2: l.coBorrowerPhone2,
            phone3: l.coBorrowerPhone3,
            email: l.coBorrowerEmail,
            address1: l.coBorrowerAddress1,
            address2: l.coBorrowerAddress2,
            city: l.coBorrowerCity,
            state: l.coBorrowerState,
            zip: l.coBorrowerZip
        } as IAgent;

        let collaterals: Array<ICollateral> = [];
        if (l.collaterals) {
            l.collaterals.forEach((elem) => {
                let c = elem as ICollateral;

                c.collateralDescription = elem.description;
                c.collateralPercentage = elem.percentage;
                c.collateralYear = elem.year;
                c.collateralVin = elem.vin;
                c.collateralCode = elem.code;
                c.insuranceExpirationDate = elem.insuranceExpirationDate;
                c.insuranceExpirationDateText =
                    elem.insuranceExpirationDateText;

                collaterals.push(c);
            });
        }

        temp.collaterals = collaterals;

        if (l.updatedDate) {
            temp.updateDate = new Date(l.updateDate);
        }

        return temp;
    }

    private _mapInsurance(re: any): IInsurance[] {
        let arr: IInsurance[] = [];
        return re.map((i) => {
            let temp = {} as IInsurance;
            temp.loanId = i.loanId;
            temp.collateralId = i.collateralId;
            temp.insurer = i.insurer;
            temp.documentSource = i.documentSource;
            // temp.deductible = i.deductible;
            temp.exposureReasonName = i.exposureReasonName;
            temp.deficiencyReason = i.deficiencyReason;
            temp.documentDate =
                i.documentDate ? this._dateOrString(i.documentDate) : null;
            temp.effectiveDate =
                i.effectiveDate ? this._dateOrString(i.effectiveDate) : null;
            temp.createDate =
                i.createDate ? this._dateOrString(i.createDate) : null;
            temp.expirationDate =
                i.expirationDate ? this._dateOrString(i.expirationDate) : null;
            temp.expirationDateText = i.expirationDateText;
            temp.coverageAmount = i.coverageAmount;
            temp.coverageTypeCode = i.coverageTypeCode;
            temp.coverageTypeName = i.coverageTypeName;
            temp.insuranceStatusCode = i.insuranceStatusCode;
            temp.insuranceStatusName = i.insuranceStatusName;
            temp.policyNumber = i.policyNumber;
            temp.collisionDeductible = i.collisionDeductible;
            temp.comprehensiveDeductible = i.comprehensiveDeductible;
            temp.namedInsured = i.namedInsured;
            temp.documentId = i.documentId;
            temp.productLine = i.productLine;
            temp.loanConditionCode = i.loanConditionCode;
            temp.loanConditionName = i.loanConditionName;
            temp.isWatchedByCurrentUser = i.isWatchedByCurrentUser;

            if (temp.documentId) {
                temp.documentFileUrl = `${API_URL}/api/v1/collaterals/${
                    temp.collateralId
                }/documents/${temp.documentId.replace(/#/g, "%23")}.pdf`;
            }

            let a: IAgent = {} as IAgent;
            a.name = i.agentName;
            a.phone1 = i.agentPhone1;
            a.phone2 = i.agentPhone2;
            a.email = i.agentEmail;
            a.address1 = i.agentAddress1;
            a.address2 = i.agentAddress2;
            a.city = i.agentCity;
            a.state = i.agentState;
            a.zip = i.agentZip;
            temp.agent = a;

            temp.deficiencies = i.deficiencyDescriptions.map(
                (description: string) => ({ description })
            );

            return temp;
        });
    }
}
